import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api } from "../../api/client";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import { LoadFailureNotice } from "../ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { DhEmpty, DhPanel } from "../../pages/downhole/DownholeBits";

/**
 * The Cost report tab on /rentals/maintenance (#815): one row per rental asset
 * from GET /surface-rentals/maintenance/cost-report, read from the
 * `asset_cost_summary` view (0494).
 *
 * EVERY FIGURE IS THE API'S. A work order whose three costs are all blank has
 * NO COST yet; while an asset has one, its cost, profit and ratio arrive null,
 * and while any work order anywhere has one the summary total arrives null.
 * Those render as —, never $0, and nothing is added up here: a browser sum of
 * the rows would be a second answer to the API's question.
 *
 * `summary` sits at the TOP level of the response, beside `data` — not inside it.
 */

interface CostReportRow {
  asset_id: string;
  asset_name: string | null;
  asset_type: string | null;
  serial_number: string | null;
  lifetime_revenue: number | null;
  lifetime_maintenance_cost: number | null;
  lifetime_profit: number | null;
  /** Already a percentage (`cost / revenue * 100`, one decimal), not a fraction. */
  maintenance_cost_ratio: number | null;
  total_work_orders: number | null;
  work_orders_without_cost: number | null;
}

interface CostReport {
  rows: CostReportRow[];
  without_cost: number | null;
  total_maintenance_cost: number | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

function toRow(r: Record<string, unknown>): CostReportRow {
  return {
    asset_id: String(r.asset_id ?? ""),
    asset_name: str(r.asset_name),
    asset_type: str(r.asset_type),
    serial_number: str(r.serial_number),
    lifetime_revenue: parseMoney(r.lifetime_revenue),
    lifetime_maintenance_cost: parseMoney(r.lifetime_maintenance_cost),
    lifetime_profit: parseMoney(r.lifetime_profit),
    maintenance_cost_ratio: parseMoney(r.maintenance_cost_ratio),
    total_work_orders: parseMoney(r.total_work_orders),
    work_orders_without_cost: parseMoney(r.work_orders_without_cost),
  };
}

/** "1 work order has no cost" / "3 work orders have no cost". */
export function withoutCostLine(n: number): string {
  return n === 1 ? "1 work order has no cost" : `${n} work orders have no cost`;
}

const TH = "px-4 py-3 text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted";
const TD = "px-4 py-3 text-sm text-fieldiq-text";
const TD_NUM = "whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text";

export default function MaintenanceCostReport() {
  const query = useQuery({
    queryKey: ["rentals", "maintenance", "cost-report"],
    queryFn: async (): Promise<CostReport> => {
      const body = ((await api.rentals.maintenance.costReport()).data ?? {}) as {
        data?: unknown;
        summary?: { work_orders_without_cost?: unknown; total_maintenance_cost?: unknown } | null;
      };
      const rows = Array.isArray(body.data) ? (body.data as Record<string, unknown>[]).map(toRow) : [];
      return {
        rows,
        without_cost: parseMoney(body.summary?.work_orders_without_cost),
        total_maintenance_cost: parseMoney(body.summary?.total_maintenance_cost),
      };
    },
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(query) != null;
  const loading = query.isLoading && !failed;
  const report = query.data;

  return (
    <DhPanel eyebrow="Cost report" title="Maintenance cost by asset" testId="maintenance-cost-report">
      {failed ? (
        <LoadFailureNotice error={queryFailureError(query)} subject="the maintenance cost report" />
      ) : loading || !report ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading cost report" />
        </div>
      ) : (
        <>
          <div className="space-y-1 border-b border-fieldiq-border px-4 py-3 text-sm">
            <p className="text-fieldiq-text">
              <span className="text-fieldiq-muted">Total maintenance cost </span>
              <span data-testid="cost-report-total" className="font-mono font-extrabold text-white">
                {fmtCurrency(report.total_maintenance_cost)}
              </span>
            </p>
            {report.without_cost != null && report.without_cost > 0 && (
              <p data-testid="cost-report-without-cost" className="text-xs text-fieldiq-muted">
                {withoutCostLine(report.without_cost)}
              </p>
            )}
          </div>
          {report.rows.length === 0 ? (
            <DhEmpty>No rental assets to report on.</DhEmpty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                  <tr>
                    <th scope="col" className={TH}>Asset</th>
                    <th scope="col" className={`${TH} text-right`}>Work orders</th>
                    <th scope="col" className={`${TH} text-right`}>Maintenance cost</th>
                    <th scope="col" className={`${TH} text-right`}>Revenue</th>
                    <th scope="col" className={`${TH} text-right`}>Profit</th>
                    <th scope="col" className={`${TH} text-right`}>Cost ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.asset_id} data-testid="cost-report-row" className="border-t border-fieldiq-border">
                      <td className={TD}>
                        <p className="font-bold">{r.asset_name ?? "—"}</p>
                        <p className="font-mono text-[11px] text-fieldiq-muted">{r.serial_number ?? "—"}</p>
                      </td>
                      <td className={TD_NUM}>
                        {r.total_work_orders ?? "—"}
                        {r.work_orders_without_cost != null && r.work_orders_without_cost > 0 && (
                          <p data-testid="cost-report-row-without-cost" className="text-[11px] font-sans text-fieldiq-muted">
                            {r.work_orders_without_cost} without cost
                          </p>
                        )}
                      </td>
                      <td className={TD_NUM}>{fmtCurrency(r.lifetime_maintenance_cost)}</td>
                      <td className={TD_NUM}>{fmtCurrency(r.lifetime_revenue)}</td>
                      <td className={TD_NUM}>{fmtCurrency(r.lifetime_profit)}</td>
                      <td className={TD_NUM}>{r.maintenance_cost_ratio == null ? "—" : `${r.maintenance_cost_ratio}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </DhPanel>
  );
}
